import { Entity } from '../../entity';
import { ActivityEntityName } from '../../shared/activity.entity.name';
import { DayShortName } from '../../shared/schedule.entity';
import { ServiceLineDefaultWindow, ServiceLineJobDefaults } from '../service.plan.template/service.line.defaults';
import { ServicePlanBlackoutRange } from '../service.plan/service.plan.blackout.range';
import { ScheduleRule } from '../../../scheduling/schedule.rule';
import { PlannedTaskOrigin, PlannedTaskScheduleExceptions, PlannedWorkAttachment, VisitTaskChange } from './planned.task';

/**
 * One row of the unified planned-work outline, whatever parent produced it.
 *
 * This is the union of what the three row types used to declare — `ServicePlanOutlineRow`'s dates and
 * blackouts, `ServicePlanTemplateOutlineRow`'s bare definition, `VisitOutlineRow`'s change state —
 * because there is now ONE projection behind all three parents. A column a given parent cannot
 * populate is simply absent on its rows: a template row carries no `effectiveFrom`, a Visit row no
 * `schedule`, a standing row none of the exception columns. That absence is the signal, and it is why
 * every parent-specific field is optional rather than split across three interfaces that would have
 * to be kept in step by hand — which is exactly what the three they replace failed to do.
 *
 * In the MODEL rather than beside the projection because both halves read it: the API strategy emits
 * these rows and the web's outline kit and Visit board consume them.
 */
export interface PlannedTaskOutlineRow extends Entity {
    attachment?: PlannedWorkAttachment;
    rowId: string;
    /** Absent on a top-level row — every line row, and a Visit's ROOT TASKS, which have no line row. */
    parentId?: string;
    hasChildren: boolean;
    sourceEntity: ActivityEntityName;
    sourceId: string;
    /** The owning Service Line, decorating every row including a Visit's, which draws no line row. */
    serviceLine?: Entity;
    name: string;
    sortOrder: string;
    description?: string;
    isSubtask?: boolean;
    mandatory?: boolean;
    duration?: number;
    evidence?: string[];
    defaultResources?: Entity[];
    defaultContractor?: Entity;
    workCategory?: Entity;
    items?: number;

    /** Standing rows only: the authored cadence and its task-level exceptions. */
    schedule?: ScheduleRule;
    scheduleExceptions?: PlannedTaskScheduleExceptions;
    /** YYYY-MM-DD cadence anchor when the task does not inherit the line start. */
    startsOn?: string;
    /** YYYY-MM-DD last completion; drives the afterService next due. */
    lastCompleted?: string;

    /** Live plan rows only: the contract's own dates, working days and blackouts. */
    effectiveFrom?: string;
    /** Line rows: the line's own working week, on live and template lines alike. */
    workingDays?: DayShortName[];
    blackoutRanges?: ServicePlanBlackoutRange[];
    timezone?: string;
    window?: ServiceLineDefaultWindow;
    jobDefaults?: ServiceLineJobDefaults;

    /** Which owner a row was sourced from — the grouping lane on a Visit's tree. */
    origin?: PlannedTaskOrigin;

    /** Visit rows only: what this attendance did to the row, and the exception behind it. */
    visitChange?: VisitTaskChange;
    exceptionId?: string;
    visitReadonly?: boolean;
    badges?: PlannedTaskOutlineRowBadge[];
}

/**
 * One resolved trailing chip on an outline row.
 *
 * Resolved by whoever BUILDS the row, not by the renderer. The shared row renderer draws every
 * planned-work outline and must not learn any one parent's vocabulary: teaching it to read
 * `visitChange` would put Visit exception semantics into the component the plan and template rows
 * also use. No colour is carried — that is presentation, and the renderer's chip lane owns it.
 */
export interface PlannedTaskOutlineRowBadge {
    id: string;
    label: string;
    tooltip?: string;
    iconLeft?: string;
    /** A badge that needs someone to act. The renderer's chip lane owns the colour; this only says which kind. */
    tone?: 'warning';
}

/** Bulk Move to line — reparent selected tasks as roots on another Service Line of the same plan. */
export interface PlannedTaskOutlineReparentRequest {
    taskIds: string[];
    targetLineId: string;
}

export interface PlannedTaskOutlineReparentLineState {
    id: string;
    hasChildren: boolean;
}

export interface PlannedTaskOutlineReparentParentState {
    id: string;
    lineId: string;
    hasChildren: boolean;
}

export interface PlannedTaskOutlineReparentResult {
    moved: number;
    targetLineId: string;
    lines: PlannedTaskOutlineReparentLineState[];
    parents: PlannedTaskOutlineReparentParentState[];
}

/** Contractor first, then people. Deduped by `_id`. */
export function outlineCrewEntities(contractor?: Entity | null, resources?: readonly Entity[] | null): Entity[] {

    const seen = new Set<string>();
    const crew: Entity[] = [];
    for (const item of [contractor ?? undefined, ...(resources ?? [])]) {
        if (!item) continue;
        const id = item._id || item.name;
        if (!id || seen.has(id)) continue;
        seen.add(id);
        crew.push(item);
    }

    return crew;
}
